import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Bounce, toast } from 'react-toastify';
import ProjectTaskTable from '../../../components/DashboardComponents/ProjectTaskTable';
import {
  CreateTaskModal,
  JoinShadowSessionModal,
  StartShadowSessionModal,
  StartSoloSessionModal,
} from '../../../components/Modals';
import Pagination from '../../../components/Listing/Pagination';
import { ICONS } from '../../../constants/icons';
import { get, patch } from '../../../api/axios';
import endpoints from '../../../api/endpoints';
import { getApiErrorMessage } from '../../../helpers/apiError';
import { getUserAvatarUrl } from '../../../helpers/userAvatar';
import { getUserFullName } from '../../../helpers/userDisplay';
import {
  createProjectSession,
  getProjectCollaborators,
  joinProjectSession,
  resolveTaskSessionAction,
  taskMatchesOngoingSession,
  withTaskOngoingSession,
} from '../../../helpers/session';
import { projectTabReturnTo } from '../../../helpers/sessionReturnTo';
import { useSessionDetailPlayer } from '../../../components/FloatingPlayer';

const PAGE_SIZE = 10;

const STATUS_API_BY_LABEL = {
  'In Progress': 'in_progress',
  Completed: 'completed',
  'Not Started': 'pending',
};

const mapApiStatusLabel = (value) => {
  const key = String(value || '')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (key === 'in_progress') return 'In Progress';
  if (key === 'completed') return 'Completed';
  if (key === 'pending' || key === 'not_started') return 'Not Started';
  return null;
};

const applyTaskStatus = (task, label) => {
  const completed = label === 'Completed';
  const sessionAction = resolveTaskSessionAction(task);
  return {
    ...task,
    status: label,
    completed,
    sessionAction: completed ? 'done' : sessionAction,
    action: completed ? 'done' : sessionAction === 'start' ? 'start' : 'continue',
  };
};

const markTaskSessionJoined = (task, sessionId) =>
  withTaskOngoingSession(task, {
    sessionId,
    sessionStatus: 'active',
    userStatus: 'joined',
  });

const clearTaskSessionIfMatches = (task, leftSessionId) =>
  taskMatchesOngoingSession(task, leftSessionId)
    ? withTaskOngoingSession(task)
    : task;

const parseDateOnly = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const formatDueDate = (value) => {
  const date = parseDateOnly(value);
  return date
    ? date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      })
    : 'Ongoing';
};

const formatHourValue = (value) => {
  const hours = Number(value);
  return Number.isFinite(hours) ? Number(hours.toFixed(2)) : 0;
};

const collaboratorDisplayName = (collaborator) =>
  getUserFullName(collaborator?.user || collaborator) || null;

const mapCollaborators = (task) => {
  const list = Array.isArray(task.latest_collaborators)
    ? task.latest_collaborators
    : Array.isArray(task.collaborators)
      ? task.collaborators
      : [];

  return list
    .map((collaborator) => ({
      id: collaborator.id || collaborator.user?.id,
      userId: collaborator.user_id || collaborator.user?.id || '',
      email: collaborator.user?.email || collaborator.email || '',
      name: collaboratorDisplayName(collaborator),
      src: getUserAvatarUrl(collaborator?.user),
    }))
    .filter((person) => person.name || person.src);
};

const mapDashboardTask = (task, description) => {
  const estimated = formatHourValue(task.estimated_hours ?? task.estimated);
  const logged = formatHourValue(
    task.total_hours_logged ?? task.logged_hours ?? task.logged
  );
  const remaining = Math.max(0, Number((estimated - logged).toFixed(2)));
  const progress = estimated > 0 ? Math.min(100, (logged / estimated) * 100) : 0;
  const collaborators = mapCollaborators(task);
  const totalCollaborators = Number(task.total_collaborators);
  const collaboratorTotal = Number.isFinite(totalCollaborators)
    ? totalCollaborators
    : collaborators.length;

  const statusKey = String(task.status || '').toLowerCase().replace(/[\s-]+/g, '_');
  const statusLabel =
    {
      in_progress: 'In Progress',
      completed: 'Completed',
      pending: 'Not Started',
      not_started: 'Not Started',
    }[statusKey] || 'In Progress';

  const completed = statusLabel === 'Completed';
  const sessionAction = resolveTaskSessionAction(task);

  return {
    id: task.id,
    title: task.title || 'Untitled Task',
    status: statusLabel,
    completed,
    dueDate: formatDueDate(task.occurrence_date || task.end_date),
    occurrenceDate: (() => {
      const raw = task.occurrence_date || task.end_date || '';
      const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
      return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
    })(),
    estimated,
    logged,
    progress,
    collaborators,
    totalCollaborators: collaboratorTotal,
    description: description || '',
    estimated_hours: estimated,
    total_hours_logged: logged,
    total_collaborators: collaboratorTotal,
    onGoingSessionId: task.on_going_session_id || null,
    on_going_session_id: task.on_going_session_id || null,
    onGoingSessionStatus: task.on_going_session_status || null,
    on_going_session_status: task.on_going_session_status || null,
    onGoingSessionUserStatus: task.on_going_session_user_status || null,
    on_going_session_user_status: task.on_going_session_user_status || null,
    sessionAction: completed ? 'done' : sessionAction,
    action: completed
      ? 'done'
      : sessionAction === 'start'
        ? 'start'
        : 'continue',
    timeTracking: {
      total: estimated,
      logged,
      remaining,
    },
    sessionActivity: [],
  };
};

const mapDashboardGroup = (member, currentUserId) => {
  const user = member.user || {};
  const userId = String(user.id || member.user_id || '');
  const isYou = Boolean(currentUserId && userId === String(currentUserId));
  const role =
    member.role_types?.[0]?.public_name ||
    member.role_types?.[0]?.name ||
    'Member';

  return {
    id: member.id,
    owner: {
      id: userId,
      memberId: member.id,
      name: getUserFullName(user, { fallback: 'Unknown' }),
      avatar: getUserAvatarUrl(user),
      role,
      roleTone: isYou ? 'blue' : 'purple',
      isYou,
    },
    tasks: (member.project_tasks || []).map((task) =>
      mapDashboardTask(task, member.description)
    ),
  };
};

/**
 * All Tasks tab — grouped task table matching Figma node 40004192-3493.
 * GET /api/v1/projects/:projectId/tasks/dashboard
 */
const ProjectTasksTab = () => {
  const { projectId: routeProjectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { openSessionDetail, isOpen, sessionId, minimize } =
    useSessionDetailPlayer();
  const { project, registerTabRefresh } = useOutletContext() || {};
  const projectId = routeProjectId || project?.id || '';
  const tasksReturnTo = useMemo(
    () => ({
      returnTo: projectTabReturnTo(location.pathname, 'tasks', projectId),
      returnLabel: 'Redirecting to tasks...',
    }),
    [location.pathname, projectId],
  );
  const currentUser = useSelector((state) => state.auth.currentUser);
  const currentUserId = currentUser?.id;
  const projectCollaborators = useMemo(
    () =>
      getProjectCollaborators(project, currentUser, {
        excludeCurrentUser: true,
      }),
    [project, currentUser]
  );

  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sessionModal, setSessionModal] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isJoiningSession, setIsJoiningSession] = useState(false);

  const openAddTask = useCallback(() => setIsCreateTaskOpen(true), []);
  const closeAddTask = useCallback(() => setIsCreateTaskOpen(false), []);
  const closeSessionModal = () => {
    setSessionModal(null);
    setActiveTask(null);
  };

  const handleStatusChange = async (task, nextLabel) => {
    const apiStatus = STATUS_API_BY_LABEL[nextLabel];
    if (!projectId || !task?.id || !apiStatus || task.status === nextLabel) {
      return;
    }

    const previous = task;
    const nextTask = applyTaskStatus(task, nextLabel);
    setGroups((prev) =>
      prev.map((group) => ({
        ...group,
        tasks: (group.tasks || []).map((item) =>
          item.id === task.id ? nextTask : item
        ),
      }))
    );
    setActiveTask((prev) =>
      prev && String(prev.id) === String(task.id) ? nextTask : prev
    );

    const response = await patch(
      endpoints.updateTaskStatus(projectId, task.id),
      { status: apiStatus }
    );

    if (response?.data?.success) {
      const raw = response.data.result;
      const payload = raw?.data || raw?.task || raw;
      if (payload?.status) {
        const label = mapApiStatusLabel(payload.status) || nextLabel;
        const mapped = applyTaskStatus(previous, label);
        setGroups((prev) =>
          prev.map((group) => ({
            ...group,
            tasks: (group.tasks || []).map((item) =>
              item.id === task.id ? mapped : item
            ),
          }))
        );
        setActiveTask((prev) =>
          prev && String(prev.id) === String(task.id) ? mapped : prev
        );
      }
      return;
    }

    setGroups((prev) =>
      prev.map((group) => ({
        ...group,
        tasks: (group.tasks || []).map((item) =>
          item.id === task.id ? previous : item
        ),
      }))
    );
    setActiveTask((prev) =>
      prev && String(prev.id) === String(task.id) ? previous : prev
    );
    toast.error(getApiErrorMessage(response, 'Failed to update status'), {
      position: 'bottom-center',
      theme: 'colored',
      transition: Bounce,
    });
  };

  const handleStartSession = (task, type) => {
    if (!task) return;

    const sessionAction = resolveTaskSessionAction(task);
    const ongoingSessionId =
      task.onGoingSessionId || task.on_going_session_id || null;

    if (sessionAction === 'view' && ongoingSessionId) {
      const floatingSessionId = sessionId ? String(sessionId) : '';
      const clickedId = String(ongoingSessionId);
      if (isOpen && floatingSessionId && clickedId !== floatingSessionId) {
        minimize?.();
        navigate(`/projects/${projectId}/sessions/${clickedId}`);
        return;
      }
      openSessionDetail({
        sessionId: ongoingSessionId,
        projectId,
        project,
        ...tasksReturnTo,
      });
      return;
    }

    setActiveTask(task);
    if (sessionAction === 'continue') {
      setSessionModal('join');
      return;
    }
    setSessionModal(type === 'shadow' ? 'shadow' : 'solo');
  };

  const fetchDashboard = useCallback(async () => {
    if (!projectId) {
      setGroups([]);
      setTotalItems(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const response = await get(endpoints.getProjectTasksDashboard(projectId), {
      params: {
        page: currentPage,
        per_page: PAGE_SIZE,
        page_size: PAGE_SIZE,
      },
    });

    if (response?.data?.success) {
      const result = response.data.result || {};
      const rows = (result.data || []).map((member) =>
        mapDashboardGroup(member, currentUserId)
      );
      const total = result.total ?? rows.length;
      const perPage = result.per_page || result.page_size || PAGE_SIZE;

      setGroups(rows);
      setTotalItems(total);
      setPageSize(perPage);

      const totalPages = Math.max(1, Math.ceil(total / perPage));
      if (currentPage > totalPages) {
        setCurrentPage(totalPages);
      }
    } else if (response) {
      setGroups([]);
      setTotalItems(0);
      setError(getApiErrorMessage(response, 'Failed to load tasks'));
    }

    setIsLoading(false);
  }, [projectId, currentPage, currentUserId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (typeof registerTabRefresh !== 'function') return undefined;
    return registerTabRefresh(fetchDashboard);
  }, [registerTabRefresh, fetchDashboard]);

  // Keep Start / Session Joined / Join Session labels in sync after exit.
  useEffect(() => {
    const handleSessionLeft = (event) => {
      const leftSessionId = event?.detail?.sessionId;
      if (!leftSessionId) return;

      const patchTask = (task) => clearTaskSessionIfMatches(task, leftSessionId);
      setGroups((prev) =>
        prev.map((group) => ({
          ...group,
          tasks: (group.tasks || []).map(patchTask),
        }))
      );
      setActiveTask((prev) => (prev ? patchTask(prev) : prev));
    };

    window.addEventListener('session-left', handleSessionLeft);
    return () => window.removeEventListener('session-left', handleSessionLeft);
  }, []);

  const handleSaveTask = () => {
    closeAddTask();
    fetchDashboard();
  };

  const createSession = async ({ type, note, userIds = [], title } = {}) => {
    if (!projectId || isCreatingSession) return null;

    setIsCreatingSession(true);
    try {
      const taskId = activeTask?.id;
      const result = await createProjectSession({
        type,
        projectId,
        note,
        userIds,
        title: taskId
          ? title || activeTask?.title || activeTask?.name
          : undefined,
        sessionTarget: taskId ? 'task' : 'project',
        targetIds: taskId ? [taskId] : [projectId],
      });

      if (!result.ok) {
        if (result.reason === 'empty_note') {
          toast.error('Please add a working note before starting the session.', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
        } else if (result.response) {
          toast.error(result.response?.data?.message || 'Failed to create session', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
        }
        return null;
      }

      toast.success(
        type === 'solo' ? 'Solo session created.' : 'Shadow session created.',
        {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        }
      );
      const createdSessionId = result.created?.id || null;
      if (createdSessionId && taskId) {
        const patch = (task) =>
          String(task.id) === String(taskId)
            ? markTaskSessionJoined(task, createdSessionId)
            : task;
        setGroups((prev) =>
          prev.map((group) => ({
            ...group,
            tasks: (group.tasks || []).map(patch),
          }))
        );
      }
      closeSessionModal();
      if (createdSessionId) {
        openSessionDetail({
          sessionId: createdSessionId,
          projectId,
          project,
          ...tasksReturnTo,
        });
      }
      return result.created;
    } catch (err) {
      if (err?.response || err?.code) {
        return null;
      }
      toast.error(err?.message || 'Failed to create session', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      return null;
    } finally {
      setIsCreatingSession(false);
    }
  };

  const joinSession = async ({ note } = {}) => {
    if (isJoiningSession) return null;

    const sessionId =
      activeTask?.onGoingSessionId || activeTask?.on_going_session_id;
    if (!sessionId) {
      toast.error('No ongoing session found for this task.', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      return null;
    }

    setIsJoiningSession(true);
    try {
      const result = await joinProjectSession({ sessionId, note });

      if (!result.ok) {
        if (result.reason === 'empty_note') {
          toast.error('Please add a working note before joining the session.', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
        } else if (result.response) {
          toast.error(result.response?.data?.message || 'Failed to join session', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
        }
        return null;
      }

      toast.success('Joined session.', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      const joinedTaskId = activeTask?.id;
      if (joinedTaskId) {
        const patch = (task) =>
          String(task.id) === String(joinedTaskId)
            ? markTaskSessionJoined(task, sessionId)
            : task;
        setGroups((prev) =>
          prev.map((group) => ({
            ...group,
            tasks: (group.tasks || []).map(patch),
          }))
        );
      }
      closeSessionModal();
      openSessionDetail({ sessionId, projectId, project, ...tasksReturnTo });
      return result.joined;
    } catch (err) {
      if (err?.response || err?.code) {
        return null;
      }
      toast.error(err?.message || 'Failed to join session', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      return null;
    } finally {
      setIsJoiningSession(false);
    }
  };

  return (
    <>
      <div className="project-detail__table-wrap">
        <ProjectTaskTable
          groups={groups}
          isLoading={isLoading}
          error={error}
          playIconSrc={ICONS.play}
          plusIconSrc={ICONS.plus}
          chevronIconSrc={ICONS.chevronDown}
          arrowIconSrc={ICONS.arrowRightBlack}
          onAddTask={openAddTask}
          onStartSession={handleStartSession}
          onStatusChange={handleStatusChange}
          onCollaboratorsAdded={fetchDashboard}
          onTaskUpdated={() => fetchDashboard()}
          onTaskDeleted={() => fetchDashboard()}
          onViewTasks={(group) => {
            if (!projectId || !group?.owner?.id) return;
            navigate(`/projects/${projectId}/tasks/${group.id}`);
          }}
          onOpenTask={(task, owner) => {
            const memberId = owner?.memberId;
            if (!projectId || !memberId || !task?.id) return;
            navigate(`/projects/${projectId}/tasks/${memberId}/detail/${task.id}`);
          }}
        />
      </div>
      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Members"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
      />
      <CreateTaskModal
        isOpen={isCreateTaskOpen}
        onClose={closeAddTask}
        onSave={handleSaveTask}
        projectId={projectId}
        project={project}
        projectStartDate={project?.startDate || project?.start_date || ''}
        projectEndDate={project?.endDate || project?.end_date || ''}
      />
      <StartSoloSessionModal
        isOpen={sessionModal === 'solo'}
        onClose={closeSessionModal}
        project={project}
        task={activeTask}
        onStart={({ note, title }) =>
          createSession({ type: 'solo', note, title, userIds: [] })
        }
        isSubmitting={isCreatingSession}
      />
      <StartShadowSessionModal
        isOpen={sessionModal === 'shadow'}
        onClose={closeSessionModal}
        project={project}
        task={activeTask}
        collaborators={projectCollaborators}
        onStart={({ note, userIds, title }) =>
          createSession({ type: 'shadow', note, userIds, title })
        }
        isSubmitting={isCreatingSession}
      />
      <JoinShadowSessionModal
        isOpen={sessionModal === 'join'}
        onClose={closeSessionModal}
        project={project}
        collaborators={activeTask?.collaborators || []}
        onJoin={joinSession}
        isSubmitting={isJoiningSession}
      />
    </>
  );
};

export default ProjectTasksTab;
